import { apiFetch, rawRequest } from "./api";
import type { PunchType, TimeclockConfig } from "@eagledispatch/shared";

let cachedNfcRequired: { value: boolean; expiresAt: number } | null = null;

/**
 * Fetches tenant timeclock NFC configuration, cached for 30 seconds.
 */
export async function fetchNfcRequired(): Promise<boolean> {
  const now = Date.now();
  if (cachedNfcRequired && cachedNfcRequired.expiresAt > now) {
    return cachedNfcRequired.value;
  }
  const config = await apiFetch<TimeclockConfig>("/api/v1/timeclock/config");
  cachedNfcRequired = {
    value: config.nfcRequired,
    expiresAt: now + 30_000,
  };
  return config.nfcRequired;
}

/**
 * SPEC-70 §2.6 — pre-auth gate probe. FAILS CLOSED (mirrors fetchNfcRequired's
 * PunchScreen precedent): if the config is unreachable, scan anyway — a
 * spurious scan prompt is cheap; a silent gate surprise is a dead driver
 * login (r1 BUG #6a). The reactive 401 branch (#6b) covers a cached probe
 * straddling a flag flip.
 */
export async function fetchLoginNfcRequired(): Promise<boolean> {
  try {
    const config = await apiFetch<TimeclockConfig>("/api/v1/timeclock/config");
    return config.nfcLoginRequired === true;
  } catch {
    return true;
  }
}

/** SPEC-46 §2.5 — the dispatcher clock-in gets a hard 8s budget. */
export const GATE_PUNCH_TIMEOUT_MS = 8000;

export type SubmitPunchResult =
  | { ok: true; deduped?: boolean; punchedAt?: string }
  | {
      ok: false;
      kind: "invalid_tag" | "auth" | "error";
      message?: string;
      code?: string;
    };

/**
 * Map a failed punch's machine code (SPEC-46 §2.3a) to dispatcher-facing
 * toast text — the §2.5 `toastFor` table. Lives here (not in App.tsx) so the
 * mapping is unit-testable without mounting the whole browser surface tree.
 * Unknown/absent codes fall back to the generic hint; per rev 4 the failure
 * is a HINT and the office timecard stays authoritative.
 */
export function punchFailureToast(code?: string): string {
  if (code === "nfc_disabled") {
    return "Office has tag scanning turned off — no clock-in recorded";
  }
  if (code === "forbidden") {
    return "Not authorized to clock in";
  }
  return "Clock-in failed — contact office";
}

/**
 * SPEC-46 rev 6 §2.5 — the dispatcher clock-in launch. Called by App when the
 * gate hands it a verified tag uid (the gate itself no longer punches).
 *
 * Why it lives beside submitPunch rather than inline in App.tsx: the field
 * incident was an ABORT-lifetime bug (the punch's controller was owned by a
 * component that unmounted in the same commit), and this repo has no DOM test
 * harness — so the abort/timeout mechanics are only provable in a unit test if
 * they live in a plain function (same reasoning as classifyReplayOutcome and
 * mapWebAuthnError).
 *
 * The punch is fire-and-forget: the console opens without waiting for it. The
 * ONLY things that abort it are (a) the 8s budget below and (b) a newer gate
 * scan replacing it. A client abort does not roll back the server — the office
 * timecard stays authoritative (rev 4).
 *
 * `controllerRef` is owned by the persistent shell (App), never by the gate.
 */
export function launchGatePunch(
  uid: string,
  controllerRef: { current: AbortController | null },
  onToast: (message: string) => void,
  submit: (
    punchType: "IN" | "OUT",
    tagUid?: string,
    idempotencyKey?: string,
    signal?: AbortSignal,
  ) => Promise<SubmitPunchResult> = submitPunch,
  timeoutMs: number = GATE_PUNCH_TIMEOUT_MS,
): void {
  // Guard: submitPunch omits a falsy tagUid, so an empty uid would silently
  // become a NO_TAG clock-in. Refuse to punch instead of mislabeling it.
  if (typeof uid !== "string" || uid.length === 0) {
    onToast(punchFailureToast());
    return;
  }

  controllerRef.current?.abort();
  const controller = new AbortController();
  controllerRef.current = controller;
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  void submit("IN", uid, undefined, controller.signal)
    .then((r) => {
      clearTimeout(timeoutId);
      if (r.ok === false) {
        onToast(punchFailureToast(r.code));
      }
    })
    .catch(() => {
      clearTimeout(timeoutId);
      onToast(punchFailureToast());
    });
}

/**
 * SPEC-76 §2.2(a) — the dispatcher single-scan auto-punch. Fired by App's
 * handleLoggedIn when a password login carried a login-scan UID: one NFC tap
 * both binds the device (login gate) and clocks the dispatcher IN. Mirrors
 * launchGatePunch's abort/timeout wiring (8s budget, the persistent shell's
 * shared controller ref) but is SILENT by design on every outcome — dedup is
 * success per timeclock.ts, failures retry at the next manual punch, and the
 * office timecard stays authoritative (SPEC-46 rev 4 lesson). The only signal
 * is the returned boolean, which App uses for its one-time success toast
 * (audit I2/S6). Rides submitPunch: the punch endpoint is idempotent()-guarded
 * and a bare POST would 400 idempotency_key_required (SPEC-76 audit B1).
 */
export async function launchAutoPunch(
  uid: string,
  controllerRef: { current: AbortController | null },
  submit: (
    punchType: "IN" | "OUT",
    tagUid?: string,
    idempotencyKey?: string,
    signal?: AbortSignal,
  ) => Promise<SubmitPunchResult> = submitPunch,
  timeoutMs: number = GATE_PUNCH_TIMEOUT_MS,
): Promise<boolean> {
  // Same guard as launchGatePunch: submitPunch omits a falsy tagUid, so an
  // empty uid would silently become a NO_TAG clock-in. Refuse (false, silent).
  if (typeof uid !== "string" || uid.length === 0) {
    return false;
  }

  controllerRef.current?.abort();
  const controller = new AbortController();
  controllerRef.current = controller;
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const res = await submit("IN", uid, undefined, controller.signal);
    // A same-day dedup counts as ok — the dispatcher is already clocked in.
    return res.ok;
  } catch {
    // submitPunch already converts network errors to results; this guard
    // keeps a misbehaving injected submit from breaking the silence contract.
    return false;
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Submits a driver or dispatcher timeclock punch (IN or OUT).
 * Respects Rule 6.7: tagUid key is omitted entirely when falsy.
 * Supports raw status dispatch: 201 -> ok, 200 -> ok + deduped, non-2xx -> surfaces error code.
 * Optionally accepts an AbortSignal for timeout / cancellation.
 */
export async function submitPunch(
  punchType: PunchType,
  tagUid?: string,
  idempotencyKey?: string,
  signal?: AbortSignal,
): Promise<SubmitPunchResult> {
  const body: { punchType: PunchType; tagUid?: string } = { punchType };
  if (tagUid) {
    body.tagUid = tagUid;
  }

  const headers: Record<string, string> = {
    "Idempotency-Key": idempotencyKey || crypto.randomUUID(),
  };

  try {
    const res = await rawRequest("/api/v1/timeclock/punch", {
      method: "POST",
      body: JSON.stringify(body),
      headers,
      signal,
    });

    if (res.status === 201) {
      const body = (await res.json().catch(() => null)) as { punch?: { punchedAt?: unknown } } | null;
      const punchedAt = body?.punch?.punchedAt;
      return {
        ok: true,
        ...(typeof punchedAt === "string" ? { punchedAt } : {}),
      };
    }

    if (res.status === 200) {
      return { ok: true, deduped: true };
    }

    // Non-2xx response: parse body to extract error code and message
    let parsedBody: Record<string, unknown> = {};
    try {
      parsedBody = (await res.json()) as Record<string, unknown>;
    } catch {
      // Body empty or not JSON
    }

    const code = typeof parsedBody.error === "string" ? parsedBody.error : undefined;
    const message =
      typeof parsedBody.message === "string"
        ? parsedBody.message
        : code === "validation"
          ? "Validation failed"
          : `Request failed with status ${res.status}`;

    if (res.status === 403 && code === "invalid_tag") {
      return { ok: false, kind: "invalid_tag", message, code };
    }
    if (res.status === 401) {
      return { ok: false, kind: "auth", message, code };
    }
    return { ok: false, kind: "error", message, code };
  } catch (err: unknown) {
    return {
      ok: false,
      kind: "error",
      message: err instanceof Error ? err.message : String(err),
    };
  }
}
